/**
 * GNSS slice, the active position source and the latest parsed fix.
 *
 * The source is whatever is currently feeding the app: the simulator now, a
 * real NMEA-over-WiFi or SignalK source later. Ingest subscribes to whatever
 * sits here, so swapping the source is the only change needed to go from
 * simulated to live.
 */
import { create } from 'zustand';
import type { GnssFix, PositionSource } from '@/gnss';
import type { PositionSourceKind } from '@/store/positionSource';

interface GnssState {
  /** The source currently feeding sentences, or null when nothing is connected. */
  source: PositionSource | null;
  setSource: (source: PositionSource | null) => void;

  /**
   * Which source produced the current own-ship position: the simulator or the
   * real device receiver. Null until the first fix lands. This lets the sim feed
   * tell a genuine training position apart from a leftover real-device fix when
   * the skipper flips the source, so switching to Simulated drops the training
   * boat back on the practice area instead of gluing it to the last real fix.
   */
  positionOrigin: PositionSourceKind | null;
  setPositionOrigin: (origin: PositionSourceKind | null) => void;

  /** The most recent parsed fix, for a signal-health readout. */
  lastFix: GnssFix | null;
  /** Count of fixes received this session (a simple "is it ticking" signal). */
  fixCount: number;
  /**
   * Wall-clock time (Date.now() ms) the last fix was committed. Used to tell how
   * old the current fix is, so a dropped/stale real-device feed can be shown and
   * the boat greyed rather than sitting there looking healthy on the last position.
   * Set for every source; the staleness UI only acts on the real device feed.
   */
  lastFixWallMs: number | null;
  setFix: (fix: GnssFix) => void;

  /**
   * Measured water depth (metres) from a connected echo sounder via the NMEA
   * DPT/DBT seam, or null when no sounder is feeding. The simulator does not
   * drive this (no sim depth yet); it's here so real hardware lights up the
   * depth-sounder side of the clearing-depth alarm with no further wiring.
   */
  measuredDepthM: number | null;
  setMeasuredDepth: (m: number | null) => void;
  /**
   * Charted depth under the boat right now (least charted depth where we sit),
   * published by MapView so data fields can show depth-under-keel without
   * redoing the point-in-polygon. Null when the chart can't tell us here.
   */
  chartedDepthUnderBoatM: number | null;
  setChartedDepthUnderBoat: (m: number | null) => void;

  /**
   * Wall-clock time (Date.now() ms) the ingest last rejected a fix as a teleport
   * — an implausible position jump on the REAL device feed. The spoofing alarm
   * reads this and stands for a short window after, so the same jump detection the
   * outlier rejection already runs raises the alarm rather than recomputing it.
   * Null until one is ever seen; only ever set for the device feed (the sim is
   * trusted), matching how the outlier rejection exempts the simulator.
   */
  lastTeleportMs: number | null;
  markTeleport: () => void;
}

export const useGnssStore = create<GnssState>((set) => ({
  source: null,
  setSource: (source) => set({ source }),
  positionOrigin: null,
  setPositionOrigin: (positionOrigin) => set({ positionOrigin }),
  lastFix: null,
  fixCount: 0,
  lastFixWallMs: null,
  setFix: (fix) => set((s) => ({ lastFix: fix, fixCount: s.fixCount + 1, lastFixWallMs: Date.now() })),
  measuredDepthM: null,
  setMeasuredDepth: (measuredDepthM) => set({ measuredDepthM }),
  chartedDepthUnderBoatM: null,
  setChartedDepthUnderBoat: (chartedDepthUnderBoatM) => set({ chartedDepthUnderBoatM }),
  lastTeleportMs: null,
  markTeleport: () => set({ lastTeleportMs: Date.now() }),
}));
